import React, { createContext, useMemo, useEffect } from 'react';
import { Agent } from '@marageti/z4-sdk/lib/people';
import { Token } from '@marageti/z4-sdk/lib/auth';
import { useApiClient } from '@marageti/z4-lib';
import { ProviderProps } from '../../types';
import { ValueProps } from './types';
import {
  setAuthenticatedAgent, setIsAuthenticated, resetAuthenticatedAgent,
} from './actions';
import useAuthReducer from './use-auth-reducer';
import LocalStorageService from '../../../utils/local-storage-service';
import {
  hasAuthorizedRole,
  getMatchingUser,
} from '../../../utils/auth';

const getCookieValue = (name: string): any | undefined => {
  // First try to find environment-specific partner cookie
  const env = process.env.REACT_APP_STAGE || 'prod';
  const partnerCookieMatch = document.cookie.match(new RegExp(`(^| )${name}_partner_${env}=([^;]+)`));
  if (partnerCookieMatch) {
    try {
      return JSON.parse(decodeURIComponent(partnerCookieMatch[2]));
    } catch (error) {
      return decodeURIComponent(partnerCookieMatch[2]);
    }
  }

  // Fall back to default cookie
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  if (match) {
    try {
      return JSON.parse(decodeURIComponent(match[2]));
    } catch (error) {
      // If the cookie value is not a valid JSON string, return it as is
      return decodeURIComponent(match[2]);
    }
  }
  return undefined;
};

const shouldCallGetAgent = (personType: string | undefined, token: Token): boolean => {
  if (!personType) return true;
  if (personType === 'agent') return true;
  if (personType === 'teamMember' && token.superEmail) return true;
  return false;
};

const LocalStorageClient = new LocalStorageService();

// On localhost the user_session cookie cannot be set (it is scoped to .zicasso.com), so local dev
// has to keep relying on the localStorage token.
const isLocalhost = typeof window !== 'undefined'
  && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

// setInternalTcCookie tags a Travel Company device as internal traffic client-side, as a backup to
// the server-side cookie set by travel-service on login.
const setInternalTcCookie = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const domain = window.location.hostname.endsWith('zicasso.com') ? '; domain=.zicasso.com' : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const oneYear = 365 * 24 * 60 * 60;
  document.cookie = `zicasso_internal_tc=true; path=/; max-age=${oneYear}; SameSite=Lax${domain}${secure}`;
};

export const AuthStore = createContext({} as ValueProps);

export const AuthProvider = ({ children }: ProviderProps) => {
  const [authState, authDispatch] = useAuthReducer();
  const apiClient = useApiClient();

  const removeAuthenticatedAgent = () => {
    LocalStorageClient.removeFromLocalStorage('accessToken');
    LocalStorageClient.removeFromLocalStorage('email');
    LocalStorageClient.removeFromLocalStorage('superEmail');
    authDispatch(setIsAuthenticated(false));
    authDispatch(resetAuthenticatedAgent());
  };

  const logoutAndRemoveAuthenticatedAgent = () => apiClient.authClient.logout()
    .finally(() => {
      removeAuthenticatedAgent();
    });

  const getPersonTypeFromCookies = (): string | undefined => {
    const userSessionPartnerCookie = getCookieValue(`user_session_partner_${process.env.REACT_APP_STAGE}`);
    if (userSessionPartnerCookie?.personType) return userSessionPartnerCookie.personType;

    const userSessionCookie = getCookieValue('user_session');
    return userSessionCookie?.personType;
  };

  const getAccessToken = (): string | undefined => {
    // Require the user_session cookie: TCs who clear cookies are forced to re-login and get a fresh
    // zicasso_internal_tc cookie. The localStorage token is only used as a complement once the
    // cookie is confirmed present. getCookieValue already falls back from the partner-specific
    // cookie to the generic one. Masquerade (team member acting as agent) keeps working because the
    // masquerading session cookie carries personType 'teamMember'.
    const userSessionCookie = getCookieValue('user_session');
    const isAgentOrMasquerader = userSessionCookie?.personType === 'agent' || userSessionCookie?.personType === 'teamMember';
    const cookieSessionId = isAgentOrMasquerader ? userSessionCookie?.sessionId : undefined;

    const localStorageAccessToken = LocalStorageClient.getFromLocalStorage('accessToken');

    // On localhost the cookie can't be set, so keep the localStorage fallback for local dev only.
    if (!cookieSessionId) {
      return isLocalhost ? localStorageAccessToken ?? undefined : undefined;
    }
    return localStorageAccessToken || cookieSessionId;
  };

  const setAccessToken = (accessToken: string) => {
    // Set Bearer token. This is required for when accessing the GetToken method through a user_session cookie.
    LocalStorageClient.addToLocalStorage('accessToken', accessToken);
    apiClient.instance.setAuthToken(accessToken);
  };

  const authorizeUserFromToken = async (accessToken: string) => {
    try {
      const token = await apiClient.authClient.getToken(accessToken);
      const personType = getPersonTypeFromCookies();
      if (!shouldCallGetAgent(personType, token)) {
        removeAuthenticatedAgent();
        return;
      }
      if (token.superEmail) {
        LocalStorageClient.addToLocalStorage('superEmail', token.superEmail);
      }
      const agent = await apiClient.peopleClient.getAgent(token.email);
      const matchingUser = getMatchingUser(agent.email, agent.userInfo);
      if (!matchingUser || !hasAuthorizedRole(matchingUser.roles)) {
        throw new Error('Not Authorized');
      }
      LocalStorageClient.addToLocalStorage('email', agent.email);
      authDispatch(setAuthenticatedAgent(agent as Agent));
      authDispatch(setIsAuthenticated(true));
    } catch (error) {
      logoutAndRemoveAuthenticatedAgent();
    }
  };

  useEffect(() => {
    const accessToken = getAccessToken();
    if (accessToken) {
      if (getPersonTypeFromCookies() === 'agent') {
        setInternalTcCookie();
      }
      setAccessToken(accessToken);
      authorizeUserFromToken(accessToken);
    } else {
      logoutAndRemoveAuthenticatedAgent();
    }
  }, []);

  const value: ValueProps = useMemo(() => ({
    ...authState,
    authDispatch,
    removeAuthenticatedAgent: logoutAndRemoveAuthenticatedAgent,
    setIsAuthenticated: (isAuthenticated: boolean) => authDispatch(setIsAuthenticated(isAuthenticated)),
    setAuthenticatedAgent: (agent: Agent) => authDispatch(setAuthenticatedAgent(agent)),
  }), [authState, authDispatch]);

  return (
    <AuthStore.Provider value={value}>
      {children}
    </AuthStore.Provider>
  );
};
